import { useState, useEffect, useRef } from 'react'

// Server-anchored countdown with a localStorage cache.
// The authoritative deadline is derived from the server's stepStartedAt + timedSec,
// so clearing localStorage cannot extend the clock. localStorage only smooths reloads
// (and provides a fallback before the server has stamped stepStartedAt).
export default function useServerTimer ({ participantId, stepId, timedSec, stepStartedAt, remainingSec, onExpire }) {
  const [remaining, setRemaining] = useState(remainingSec ?? timedSec ?? null)
  const firedRef = useRef(false)
  const expireRef = useRef(onExpire)
  useEffect(() => { expireRef.current = onExpire }, [onExpire])

  useEffect(() => {
    if (timedSec == null) { setRemaining(null); return }
    firedRef.current = false
    const key = `study_timer:${participantId}:${stepId}`
    let deadline
    if (stepStartedAt) {
      deadline = stepStartedAt + timedSec * 1000          // server-authoritative
      localStorage.setItem(key, String(deadline))
    } else {
      const cached = Number(localStorage.getItem(key))
      deadline = cached || (Date.now() + (remainingSec ?? timedSec) * 1000)
      localStorage.setItem(key, String(deadline))
    }
    let id = null
    const tick = () => {
      const r = Math.max(0, Math.round((deadline - Date.now()) / 1000))
      setRemaining(r)
      if (r <= 0 && !firedRef.current) {
        firedRef.current = true
        if (id) clearInterval(id)
        localStorage.removeItem(key)
        expireRef.current && expireRef.current()
      }
    }
    tick()
    id = setInterval(tick, 1000)
    return () => { if (id) clearInterval(id) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantId, stepId, timedSec, stepStartedAt])

  return remaining
}
